import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowLeft, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button, TextInput } from '../../components';
import { LogoMark } from '../../components/branding/Logo';
import { apiClient } from '../../api/client';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await apiClient.post('/api/v1/auth/reset-password', {
        token,
        email,
        password,
        password_confirmation: confirmPassword,
      });
      setIsSuccess(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string; details?: Record<string, string[]> }; message?: string } } };

      // Extract validation errors from Laravel response
      if (error.response?.data?.error?.details) {
        const validationErrors = Object.values(error.response.data.error.details).flat();
        setError(validationErrors.join('\n'));
      } else {
        setError(error.response?.data?.error?.message || error.response?.data?.message || 'Unable to reset password. The link may have expired.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <div className="max-w-sm mx-auto">
        <div className="bg-background-card rounded-xl p-8 shadow-md text-center">
          <h2 className="text-2xl font-semibold text-text-primary mb-2">Invalid Link</h2>
          <p className="text-text-secondary mb-6">
            This password reset link is invalid or has expired.
          </p>
          <Link
            to="/forgot-password"
            className="text-primary font-medium hover:underline"
          >
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="max-w-sm mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-background-card rounded-xl p-8 shadow-md text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 500 }}
            className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-8 h-8 text-success" />
          </motion.div>
          <h2 className="text-2xl font-semibold text-text-primary mb-2">Password Reset</h2>
          <p className="text-text-secondary mb-6">
            Your password has been successfully reset.
          </p>
          <Button variant="primary" fullWidth onClick={() => navigate('/login')}>
            Sign In
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-sm mx-auto">
      <div className="bg-background-card rounded-xl p-6 md:p-8 shadow-md">
        <div className="text-center mb-6 md:mb-8">
          <LogoMark className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-3 md:mb-4 md:hidden" />
          <h2 className="text-xl md:text-2xl font-semibold text-text-primary mb-1 md:mb-2">Reset password</h2>
          <p className="text-sm md:text-base text-text-secondary">
            Enter your new password below.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
          <TextInput
            type="password"
            label="New Password"
            placeholder="Enter new password"
            icon={Lock}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
          />

          <TextInput
            type="password"
            label="Confirm Password"
            placeholder="Confirm new password"
            icon={Lock}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            autoComplete="new-password"
          />

          {error && (
            <div className="p-3 bg-error/10 border border-error/20 rounded-md">
              <p className="text-sm text-error text-center whitespace-pre-line">{error}</p>
            </div>
          )}

          <div className="pt-2">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={isLoading}
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </div>
        </form>
      </div>

      <p className="text-center mt-6 text-text-secondary">
        <Link to="/login" className="text-primary font-medium inline-flex items-center gap-1">
          <ArrowLeft size={14} />
          Back to login
        </Link>
      </p>
    </div>
  );
}
